import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { ProviderData } from '@dataTypes/provider.types'
import { AccountsSection } from '@constants/Accounts'
import {
  loadRow,
  parseRow,
  patchProvider,
  type ProviderResponse,
} from '@services/api/provider.api'
import { mapDatabaseToProviderData } from '@utils/provider.mapper'
import { readPid } from '@utils/accountSession.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'

const LOADING_CONTAINER = 'body'

type LoadState = 'idle' | 'loading' | 'ready' | 'error'

type ProfileCtx = {
  profile: ProviderData | null
  loadState: LoadState
  pid: string | null
  reload: () => Promise<void>
  savePatch: (
    _section: AccountsSection,
    partial: Partial<ProviderData>,
  ) => Promise<boolean>
}

const context = createContext<ProfileCtx | null>(null)

export function ProfileProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [profile, setProfile] = useState<ProviderData | null>(null)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [pid, setPid] = useState<string | null>(() => readPid())

  useEffect(() => {
    if (loadState !== 'loading') {
      hideLoading(LOADING_CONTAINER)
      return undefined
    }
    showLoading({ container: LOADING_CONTAINER })
    return () => hideLoading(LOADING_CONTAINER)
  }, [hideLoading, loadState, showLoading])

  const reload = useCallback(async (): Promise<void> => {
    const id = readPid()
    setPid(id)
    if (!id) {
      setProfile(null)
      setLoadState('error')
      return
    }
    setLoadState('loading')
    try {
      const row = await loadRow(id)
      if (!row) {
        setProfile(null)
        setLoadState('error')
        return
      }
      setProfile(mapDatabaseToProviderData(row))
      setLoadState('ready')
    } catch {
      setProfile(null)
      setLoadState('error')
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const savePatch = useCallback(
    async (
      _section: AccountsSection,
      partial: Partial<ProviderData>,
    ): Promise<boolean> => {
      const id = readPid()
      if (!id) return false
      showLoading({ container: LOADING_CONTAINER })
      try {
        const res: ProviderResponse = await patchProvider(id, partial)
        if (res.statusCode !== 200) return false
        const updated = parseRow(res)
        if (updated) {
          setProfile(mapDatabaseToProviderData(updated))
        } else {
          await reload()
        }
        return true
      } finally {
        hideLoading(LOADING_CONTAINER)
      }
    },
    [hideLoading, reload, showLoading],
  )

  const value = useMemo(
    () => ({
      profile,
      loadState,
      pid,
      reload,
      savePatch,
    }),
    [profile, loadState, pid, reload, savePatch],
  )

  return <context.Provider value={value}>{children}</context.Provider>
}

export function useProfile(): ProfileCtx {
  const v = useContext(context)
  if (!v) {
    throw new Error('useProfile must be used within ProfileProvider')
  }
  return v
}
